import { DollarSign, Receipt, TrendingUp, Clock } from 'lucide-react';
import type { Database } from '../../lib/database.types';

type Receipt = Database['public']['Tables']['receipts']['Row'];

interface AnalyticsCardsProps {
  receipts: Receipt[];
}

export default function AnalyticsCards({ receipts }: AnalyticsCardsProps) {
  const totalAmount = receipts
    .filter((r) => r.status === 'parsed' && r.total_amount)
    .reduce((sum, r) => sum + (r.total_amount || 0), 0);

  const parsedCount = receipts.filter((r) => r.status === 'parsed').length;
  const pendingCount = receipts.filter((r) => r.status === 'pending').length;

  const now = new Date();
  const thisMonth = receipts.filter((r) => {
    const receiptDate = r.receipt_date ? new Date(r.receipt_date) : null;
    return (
      receiptDate &&
      receiptDate.getMonth() === now.getMonth() &&
      receiptDate.getFullYear() === now.getFullYear()
    );
  });

  const thisMonthTotal = thisMonth
    .filter((r) => r.status === 'parsed' && r.total_amount)
    .reduce((sum, r) => sum + (r.total_amount || 0), 0);

  const cards = [
    {
      title: 'Total Expenses',
      value: `$${totalAmount.toFixed(2)}`,
      icon: DollarSign,
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-700',
    },
    {
      title: 'This Month',
      value: `$${thisMonthTotal.toFixed(2)}`,
      icon: TrendingUp,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
    },
    {
      title: 'Total Receipts',
      value: receipts.length.toString(),
      icon: Receipt,
      color: 'from-violet-500 to-purple-600',
      bgColor: 'bg-violet-50',
      textColor: 'text-violet-700',
    },
    {
      title: 'Pending',
      value: pendingCount.toString(),
      icon: Clock,
      color: 'from-amber-500 to-orange-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map((card) => (
        <div
          key={card.title}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-600">{card.title}</p>
              <p className="mt-2 text-3xl font-bold text-slate-800">{card.value}</p>
            </div>
            <div className={`${card.bgColor} p-3 rounded-lg`}>
              <card.icon className={`w-6 h-6 ${card.textColor}`} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
